/**
 * In-chat Nango connect chips. A retired merge override connects through Nango,
 * and a saved merge slug is translated to its Nango provider key.
 */

import { cn } from '@heidi/utils';

import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { Check } from 'lucide-react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { getWorkHostPort } from '../../work-home/work-home-ports';

import type {
  ConnectAppOption,
  ConnectAppStack,
} from '../../../contracts/projects/integrations/connectAppChips.defaults';
import { resolveConnectAppOptions } from './connectAppChips.defaults';

interface ConnectAppChipsProps {
  /** Connector / provider ids to offer. */
  connectors: readonly ConnectAppOption[] | readonly string[];
  /** Override stack; defaults to the clinician's Connected apps preference. */
  stack?: ConnectAppStack | null;
  onConnected?: (connector: string) => void;
  connectedIds?: ReadonlySet<string>;
  disabled?: boolean;
  className?: string;
  /**
   * Optional hint above the chips. Pass ``""`` to hide the default hint
   * (e.g. when a parent already shows copy).
   */
  message?: string;
}

export function ConnectAppChips({
  connectors,
  onConnected,
  connectedIds,
  disabled = false,
  className,
  message,
}: ConnectAppChipsProps) {
  const { NangoConnectButton } = getWorkHostPort('integrations');
  const stack: ConnectAppStack = 'nango';

  const options = resolveConnectAppOptions(connectors, stack);
  const [localConnected, setLocalConnected] = useState<Set<string>>(() => new Set());

  const handleConnected = useCallback(
    (connector: string) => {
      setLocalConnected((prev) => new Set(prev).add(connector));
      onConnected?.(connector);
    },
    [onConnected]
  );

  if (options.length === 0) return null;

  return (
    <div
      className={cn('flex flex-col gap-2', className)}
      data-testid="connect-app-chips"
      data-stack={stack}
    >
      {message === '' ? null : message ? (
        <TypographyP2 className="text-text-secondary">{message}</TypographyP2>
      ) : (
        <TypographyCaption className="text-text-secondary">
          <FormattedMessage
            defaultMessage="Connect an app to continue."
            id="UwzqRWMIwo"
          />
        </TypographyCaption>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const connectKey = option.providerKey ?? option.id;
          const isConnected =
            connectedIds?.has(option.id) || localConnected.has(option.id);
          if (isConnected) {
            return (
              <div
                key={option.id}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-shell px-3 py-1.5 text-sm text-text-primary"
                data-testid={`connect-app-connected-${option.id}`}
              >
                <Check className="size-3.5 text-validation-success" aria-hidden />
                {option.label}
              </div>
            );
          }
          return (
            <NangoConnectButton
              key={option.id}
              allowedIntegrations={[connectKey]}
              compact
              testId={`nango-connect-${connectKey}`}
              onConnected={() => handleConnected(option.id)}
              disabled={disabled}
            >
              <FormattedMessage
                defaultMessage="Connect {app}"
                id="D3hnxkmFfw"
                values={{ app: option.label ?? option.id }}
              />
            </NangoConnectButton>
          );
        })}
      </div>
    </div>
  );
}
